import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  Paper,
  Fade,
  Zoom,
  Badge,
  Chip,
  alpha,
  Container,
  Stack,
  Tooltip,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DiamondIcon from '@mui/icons-material/Diamond';
import WavesIcon from '@mui/icons-material/Waves';

// Animations
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const glowPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(255, 255, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

// Styled Components
const GlassAppBar = styled(AppBar)(({ theme }) => ({
  backdropFilter: 'blur(20px)',
  backgroundColor: alpha(theme.palette.background.default, 0.8),
  boxShadow: 'none',
  borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const PremiumDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.background.paper,
      0.95,
    )} 0%, ${alpha(theme.palette.primary.dark, 0.05)} 100%)`,
    backdropFilter: 'blur(20px)',
    borderRight: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    boxShadow: `20px 0 40px ${alpha(theme.palette.common.black, 0.1)}`,
  },
}));

const NavItemButton = styled(ListItemButton, {
  shouldForwardProp: prop => prop !== 'active' && prop !== 'depth',
})<{ active?: boolean; depth?: number }>(({ theme, active, depth = 0 }) => ({
  margin: theme.spacing(0.5, 1),
  padding: theme.spacing(1.2, 2),
  borderRadius: theme.spacing(1.5),
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',

  ...(active && {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
    boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
    transform: 'translateX(8px)',

    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.contrastText,
      transform: 'scale(1.1)',
    },

    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, ${alpha(
        theme.palette.common.white,
        0.2,
      )} 0%, ${alpha(theme.palette.common.white, 0)} 100%)`,
      zIndex: 1,
    },
  }),

  '&:hover': {
    transform: active ? 'translateX(8px)' : 'translateX(4px)',
    backgroundColor: active ? undefined : alpha(theme.palette.primary.main, 0.08),

    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
  },

  '& .MuiListItemIcon-root': {
    minWidth: depth === 0 ? 40 : 32,
    transition: 'all 0.3s ease',
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.common.white,
      0.1,
    )}, transparent)`,
    transform: 'translateX(-100%)',
    transition: 'transform 0.6s ease',
  },

  '&:hover::after': {
    transform: 'translateX(100%)',
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const AnimatedAvatar = styled(Avatar)(({ theme }) => ({
  animation: `${floatAnimation} 3s ease-in-out infinite`,
  border: `2px solid ${theme.palette.primary.main}`,
  boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
  transition: 'all 0.3s ease',

  '&:hover': {
    animation: `${glowPulse} 2s infinite`,
    transform: 'scale(1.1)',
  },
}));

const PremiumBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
    color: theme.palette.secondary.contrastText,
    animation: `${glowPulse} 2s infinite`,
  },
}));

export interface NavItem {
  label: string;
  path?: string;
  icon?: React.ReactNode;
  children?: NavItem[];
  badge?: number;
  disabled?: boolean;
}

export interface UserMenuProps {
  name: string;
  role: string;
  avatar?: string;
  onLogout: () => void;
  onProfile?: () => void;
  onSettings?: () => void;
}

export interface AppShellProps {
  children?: React.ReactNode;
  title?: string;
  navItems?: NavItem[];
  navigation?: NavItem[];
  sidebarNav?: NavItem[];
  userMenu?: UserMenuProps;
  notifications?: number;
  onSearch?: (query: string) => void;
}

const DRAWER_WIDTH = 280;
const COLLAPSED_DRAWER_WIDTH = 80;

const AppShell: React.FC<AppShellProps> = ({
  children,
  title = 'Resort Management',
  navItems,
  navigation,
  sidebarNav,
  userMenu,
  notifications = 0,
  onSearch,
}) => {
  const mergedNavItems = navItems || navigation || sidebarNav || [];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setCollapsed(!collapsed);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavClick = (item: NavItem) => {
    if (item.path && !item.disabled) {
      navigate(item.path);
      if (isMobile) {
        setMobileOpen(false);
      }
    }
  };

  const handleLogout = () => {
    handleMenuClose();
    userMenu?.onLogout();
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    onSearch?.(e.target.value);
  };

  const isActive = (item: NavItem) => {
    return item.path === location.pathname;
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const renderNavItems = (items: NavItem[], depth = 0) => {
    const effectiveDepth = collapsed ? 0 : depth;

    return items.map(item => (
      <Zoom in key={item.label} style={{ transitionDelay: `${depth * 50}ms` }}>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <Tooltip title={collapsed ? item.label : ''} placement="right" arrow>
            <NavItemButton
              active={isActive(item)}
              depth={effectiveDepth}
              onClick={() => handleNavClick(item)}
              disabled={item.disabled}
              sx={{
                minHeight: 48,
                justifyContent: collapsed ? 'center' : 'initial',
                px: collapsed ? 2.5 : 2,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed ? 0 : effectiveDepth === 0 ? 40 : 32,
                  justifyContent: 'center',
                  mr: collapsed ? 0 : 2,
                }}
              >
                {item.badge ? (
                  <PremiumBadge badgeContent={item.badge} color="secondary" max={99}>
                    {item.icon || <DiamondIcon />}
                  </PremiumBadge>
                ) : (
                  item.icon || <WavesIcon />
                )}
              </ListItemIcon>
              {!collapsed && (
                <>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: isActive(item) ? 600 : 400,
                    }}
                  />
                  {item.children && (
                    <KeyboardArrowDownIcon
                      sx={{
                        fontSize: 16,
                        opacity: 0.7,
                        transform: 'rotate(-90deg)',
                      }}
                    />
                  )}
                </>
              )}
            </NavItemButton>
          </Tooltip>
          {item.children && !collapsed && (
            <Fade in timeout={300}>
              <Box sx={{ pl: 2 }}>{renderNavItems(item.children, depth + 1)}</Box>
            </Fade>
          )}
        </ListItem>
      </Zoom>
    ));
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <LogoContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DiamondIcon sx={{ color: 'primary.main', fontSize: 32 }} />
          {!collapsed && (
            <Typography
              variant="h6"
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 700,
              }}
            >
              {title}
            </Typography>
          )}
        </Box>
        {!isMobile && (
          <IconButton onClick={handleDrawerToggle} size="small">
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        )}
      </LogoContainer>

      <Box sx={{ flex: 1, overflow: 'auto', py: 2, px: collapsed ? 1 : 2 }}>
        <List>{renderNavItems(mergedNavItems)}</List>
      </Box>

      {userMenu && !collapsed && (
        <Box sx={{ p: 2, borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.1,
              )} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <AnimatedAvatar src={userMenu.avatar}>{getInitials(userMenu.name)}</AnimatedAvatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {userMenu.name}
                </Typography>
                <Chip
                  label={userMenu.role}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    background: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                  }}
                />
              </Box>
            </Stack>
          </Paper>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
        <GlassAppBar
          position="fixed"
          elevation={scrolled ? 4 : 0}
          sx={{
            width: {
              md: `calc(100% - ${collapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH}px)`,
            },
            ml: { md: collapsed ? `${COLLAPSED_DRAWER_WIDTH}px` : `${DRAWER_WIDTH}px` },
            transition: theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
        >
          <Toolbar>
            <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>

            <Typography
              variant="h6"
              component="div"
              sx={{
                flexGrow: 1,
                fontWeight: 600,
                background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.primary.main} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Welcome back, {userMenu?.name?.split(' ')[0] || 'Guest'}
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              {/* Simple search input (restored) */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: alpha(theme.palette.common.white, 0.05),
                  borderRadius: theme.spacing(3),
                  padding: theme.spacing(0.5, 1.5),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={e => {
                    setSearchQuery(e.target.value);
                    onSearch?.(e.target.value);
                  }}
                  style={{ border: 'none', background: 'transparent', outline: 'none', width: 160 }}
                />
              </Box>

              <Tooltip title="Notifications">
                <IconButton color="inherit" sx={{ position: 'relative' }}>
                  <PremiumBadge badgeContent={notifications} color="secondary">
                    <NotificationsNoneIcon />
                  </PremiumBadge>
                </IconButton>
              </Tooltip>

              <Tooltip title="Settings">
                <IconButton color="inherit">
                  <SettingsIcon />
                </IconButton>
              </Tooltip>

              {userMenu && (
                <div>
                  <IconButton color="inherit" onClick={handleMenuOpen} sx={{ p: 0.5, ml: 1 }}>
                    <AnimatedAvatar src={userMenu.avatar}>
                      {getInitials(userMenu.name)}
                    </AnimatedAvatar>
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    TransitionComponent={Fade}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    PaperProps={{
                      elevation: 8,
                      sx: {
                        mt: 1,
                        minWidth: 200,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${alpha(
                          theme.palette.background.paper,
                          0.95,
                        )} 0%, ${alpha(theme.palette.primary.dark, 0.1)} 100%)`,
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      },
                    }}
                  >
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {userMenu.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {userMenu.role}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    {userMenu.onProfile && (
                      <MenuItem onClick={userMenu.onProfile}>Profile</MenuItem>
                    )}
                    {userMenu.onSettings && (
                      <MenuItem onClick={userMenu.onSettings}>Settings</MenuItem>
                    )}
                    <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                      Logout
                    </MenuItem>
                  </Menu>
                </div>
              )}
            </Stack>
          </Toolbar>
        </GlassAppBar>

        <Box
          component="nav"
          sx={{
            width: { md: collapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH },
            flexShrink: { md: 0 },
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
        >
          {isMobile ? (
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{ keepMounted: true }}
              sx={{
                '& .MuiDrawer-paper': {
                  width: DRAWER_WIDTH,
                  boxSizing: 'border-box',
                  background: theme.palette.background.default,
                },
              }}
            >
              {drawer}
            </Drawer>
          ) : (
            <PremiumDrawer
              variant="permanent"
              sx={{
                '& .MuiDrawer-paper': {
                  width: collapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH,
                  boxSizing: 'border-box',
                  overflowX: 'hidden',
                  transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                  }),
                },
              }}
              open
            >
              {drawer}
            </PremiumDrawer>
          )}
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: {
              md: `calc(100% - ${collapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH}px)`,
            },
            mt: '64px',
            transition: theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            position: 'relative',

            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '200px',
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.1,
              )} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
              clipPath: 'polygon(0 0, 100% 0, 100% 60%, 0 100%)',
              zIndex: 0,
              pointerEvents: 'none',
            },
          }}
        >
          <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
            <Fade in timeout={500}>
              <Box>{children}</Box>
            </Fade>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default AppShell;
